import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { MakeMonogram } from "@/components/opportunities/StatusBadges";
import { eur, num, pct } from "@/lib/format";

const Stat = ({ label, value, tone = "text-slate-900" }) => (
  <div className="min-w-[74px]">
    <div className="label-xs whitespace-nowrap">{label}</div>
    <div className={`mono mt-0.5 text-[12.5px] font-semibold leading-none ${tone}`}>{value}</div>
  </div>
);

const timeOnly = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const GroupHeader = ({
  label,
  count,
  stats,
  level = "make",
  open,
  onToggle,
  onRefresh,
  refreshing,
  showSummary = true,
  testid,
}) => {
  const styles = {
    make: "bg-white pl-2 text-[15px] font-bold",
    model: "bg-slate-50/70 pl-6 text-[13.5px] font-semibold",
    type: "bg-white pl-10 text-[13px] font-semibold",
  };
  return (
    <div
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-200 py-2 pr-2 ${styles[level]}`}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`${testid}-toggle`}
        className="flex min-w-0 items-center gap-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
        )}
        {level === "make" && <MakeMonogram label={label} />}
        <span className="truncate font-display tracking-tight text-slate-900">{label}</span>
        <span className="chip shrink-0 border-slate-200 bg-slate-100 normal-case tracking-normal text-slate-600">
          {num(count)} voertuigen
        </span>
      </button>

      {showSummary && (
        <div className="ml-auto flex flex-wrap items-center gap-x-5 gap-y-1.5">
          <Stat
            label="Beste Deal Score"
            value={stats.best_score === null ? "—" : `${stats.best_score} / 100`}
          />
          <Stat
            label="Beste bijdrage"
            value={eur(stats.best_contribution, { fallback: "—" })}
            tone={(stats.best_contribution ?? 0) > 0 ? "text-emerald-700" : "text-slate-900"}
          />
          <Stat label="Gem. ROI" value={pct(stats.avg_roi, { fallback: "—" })} />
          <Stat
            label="Nieuw"
            value={num(stats.new_count)}
            tone={stats.new_count ? "text-emerald-700" : "text-slate-400"}
          />
          <Stat
            label="Prijs verlaagd"
            value={num(stats.price_drop_count)}
            tone={stats.price_drop_count ? "text-amber-700" : "text-slate-400"}
          />
          <Stat label="Laatste scan" value={timeOnly(stats.last_scan_at)} />
        </div>
      )}

      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          data-testid={`${testid}-refresh`}
          className={`ml-auto flex shrink-0 items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-2.5 py-1.5 text-[11.5px] font-semibold text-slate-700 transition-colors duration-150 hover:border-blue-400 hover:text-blue-700 disabled:opacity-50 ${
            showSummary ? "ml-0" : ""
          }`}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Ververs groep
        </button>
      )}
    </div>
  );
};
